import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';

/**
 * Loads and drives the Google Identity Services and Facebook JS SDKs — browser-only and lazy:
 * the third-party scripts are injected on first use, and ONLY when the corresponding client id is
 * configured. Each method resolves with the provider token; the caller exchanges it with the API.
 *
 * Google → an ID token (JWT) via a rendered GIS button.
 * Facebook → a user access token via FB.login.
 */
@Injectable({ providedIn: 'root' })
export class SocialAuthService {
  private readonly isBrowser: boolean;
  private googleReady?: Promise<void>;
  private facebookReady?: Promise<void>;

  readonly googleClientId = environment.googleClientId;
  readonly facebookAppId = environment.facebookAppId;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Whether each provider is configured (and we're in the browser). */
  get googleEnabled(): boolean {
    return this.isBrowser && !!this.googleClientId;
  }
  get facebookEnabled(): boolean {
    return this.isBrowser && !!this.facebookAppId;
  }

  /**
   * Render the official Google button into <code>el</code>. Google calls <code>onCredential</code>
   * with an ID token when the user completes sign-in.
   */
  async renderGoogleButton(el: HTMLElement, onCredential: (idToken: string) => void): Promise<void> {
    if (!this.googleEnabled) return;
    await this.loadGoogle();
    const google = (window as unknown as { google?: any }).google;
    if (!google?.accounts?.id) return;
    google.accounts.id.initialize({
      client_id: this.googleClientId,
      callback: (resp: { credential?: string }) => {
        if (resp?.credential) onCredential(resp.credential);
      },
    });
    el.innerHTML = '';
    google.accounts.id.renderButton(el, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      logo_alignment: 'center',
      width: Math.min(el.offsetWidth || 320, 400),
    });
  }

  /** Open the Facebook login dialog and resolve with the user access token. */
  async facebookLogin(): Promise<string> {
    if (!this.facebookEnabled) throw new Error('facebook-disabled');
    await this.loadFacebook();
    const FB = (window as unknown as { FB?: any }).FB;
    if (!FB) throw new Error('facebook-unavailable');
    return new Promise<string>((resolve, reject) => {
      FB.login(
        (resp: { authResponse?: { accessToken?: string } }) => {
          const token = resp?.authResponse?.accessToken;
          if (token) resolve(token);
          else reject(new Error('facebook-cancelled'));
        },
        { scope: 'public_profile,email' },
      );
    });
  }

  // ── script loaders ──────────────────────────────────────────────────────────
  private loadGoogle(): Promise<void> {
    if (!this.googleReady) {
      // Drop the cached promise on failure (e.g. blocked by an ad/tracker blocker) so a later
      // attempt can retry instead of being stuck on a permanently-rejected promise.
      this.googleReady = this.injectScript('https://accounts.google.com/gsi/client', 'oy-gis').catch((e) => {
        this.googleReady = undefined;
        throw e;
      });
    }
    return this.googleReady;
  }

  private loadFacebook(): Promise<void> {
    if (!this.facebookReady) {
      this.facebookReady = new Promise<void>((resolve, reject) => {
        const win = window as unknown as { fbAsyncInit?: () => void; FB?: any };
        win.fbAsyncInit = () => {
          win.FB.init({ appId: this.facebookAppId, cookie: true, xfbml: false, version: 'v19.0' });
          resolve();
        };
        this.injectScript('https://connect.facebook.net/en_US/sdk.js', 'oy-fb').catch(reject);
      }).catch((e) => {
        this.facebookReady = undefined;
        throw e;
      });
    }
    return this.facebookReady;
  }

  private injectScript(src: string, id: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(id) as HTMLScriptElement | null;
      if (existing) {
        resolve();
        return;
      }
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.defer = true;
      s.id = id;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(s);
    });
  }
}
